import {
  MAX_MANIFEST_PAGE_SIZE,
  MEDIA_PROTOCOL_VERSION,
  type AvailableShare,
  type ManifestPage,
  type ManifestPageGet,
  type MediaCancel,
  type MediaComplete,
  type MediaControlMessage,
  type MediaError,
  type MediaGet,
  type MediaHello,
  type MediaHelloAck,
  type MediaProgress,
  type MediaQueued,
  type MediaStart,
  type OwnerMediaControlMessage,
  type ShareRemoved,
  type ShareUpdated,
  type SharesAvailable,
  type ViewerMediaControlMessage,
} from "./media-control";
import {
  MEDIA_KINDS,
  MEDIA_RENDITIONS,
  parseMediaDescriptor,
} from "./media-types";
import { parseClientCapabilities } from "./sharing";
import {
  ProtocolValidationError,
  parseJsonObject,
  requireArray,
  requireBoolean,
  requireByteLength,
  requireEnum,
  requireExactKeys,
  requireInteger,
  requireLiteral,
  requireNullableString,
  requireRecord,
  requireSha256,
  requireString,
  requireUuid,
} from "./validation";

const SHARE_REMOVAL_REASONS = [
  "paused",
  "revoked",
  "expired",
  "source-unavailable",
  "capability-disabled",
] as const;
const MEDIA_CANCEL_REASONS = [
  "viewer-request",
  "share-removed",
  "feature-disabled",
  "source-unavailable",
  "connection-closed",
] as const;
const MEDIA_ERROR_CODES = [
  "unauthorized",
  "not_found",
  "unsupported_rendition",
  "feature_disabled",
  "invalid_request",
  "revision_mismatch",
  "source_unavailable",
  "overloaded",
  "cancelled",
  "integrity_failed",
  "timeout",
  "internal",
] as const;

export function parseMediaControlMessage(
  value: unknown,
): MediaControlMessage {
  const record = requireRecord(value);
  requireMediaVersion(record.v);
  const type = requireString(record.type, "$.type", { maxLength: 64 });
  switch (type) {
    case "media.hello":
      return parseMediaHello(record);
    case "media.hello.ack":
      return parseMediaHelloAck(record);
    case "shares.available":
      return parseSharesAvailable(record);
    case "share.updated":
      return parseShareUpdated(record);
    case "share.removed":
      return parseShareRemoved(record);
    case "manifest.page.get":
      return parseManifestPageGet(record);
    case "manifest.page":
      return parseManifestPage(record);
    case "media.get":
      return parseMediaGet(record);
    case "media.queued":
      return parseMediaQueued(record);
    case "media.start":
      return parseMediaStart(record);
    case "media.progress":
      return parseMediaProgress(record);
    case "media.complete":
      return parseMediaComplete(record);
    case "media.cancel":
      return parseMediaCancel(record);
    case "media.error":
      return parseMediaError(record);
    default:
      throw new ProtocolValidationError(
        "unknown_type",
        `Unknown media control message type: ${type}`,
        "$.type",
      );
  }
}

export function parseViewerMediaControlMessage(
  value: unknown,
): ViewerMediaControlMessage {
  const message = parseMediaControlMessage(value);
  if (
    message.type === "media.hello"
    || message.type === "manifest.page.get"
    || message.type === "media.get"
    || message.type === "media.cancel"
  ) {
    return message;
  }
  throw wrongDirection(message.type, "viewer");
}

export function parseOwnerMediaControlMessage(
  value: unknown,
): OwnerMediaControlMessage {
  const message = parseMediaControlMessage(value);
  if (
    message.type !== "media.hello"
    && message.type !== "manifest.page.get"
    && message.type !== "media.get"
    && message.type !== "media.cancel"
  ) {
    return message;
  }
  throw wrongDirection(message.type, "owner");
}

export function parseMediaControlJson(text: string): MediaControlMessage {
  return parseMediaControlMessage(parseJsonObject(text));
}

export function parseViewerMediaControlJson(
  text: string,
): ViewerMediaControlMessage {
  return parseViewerMediaControlMessage(parseJsonObject(text));
}

export function parseOwnerMediaControlJson(
  text: string,
): OwnerMediaControlMessage {
  return parseOwnerMediaControlMessage(parseJsonObject(text));
}

export function parseAvailableShare(
  value: unknown,
  path = "$",
): AvailableShare {
  const record = requireRecord(value, path);
  requireExactKeys(
    record,
    ["shareId", "label", "revision", "capabilities"],
    [
      "collaborators",
      "originalMediaCount",
      "ownerAvatarUrl",
      "ownerEmail",
      "ownerUsername",
    ],
    path,
  );
  return {
    shareId: requireUuid(record.shareId, `${path}.shareId`),
    label: requireString(record.label, `${path}.label`, { maxLength: 200 }),
    ...(record.collaborators === undefined
      ? {}
      : {
        collaborators: requireArray(
          record.collaborators,
          `${path}.collaborators`,
          (value, collaboratorPath) => {
            const collaborator = requireRecord(value, collaboratorPath);
            requireExactKeys(
              collaborator,
              ["displayName", "mediaCount", "recipientId"],
              ["avatarUrl"],
              collaboratorPath,
            );
            return {
              ...(collaborator.avatarUrl === undefined
                ? {}
                : {
                  avatarUrl: requireString(
                    collaborator.avatarUrl,
                    `${collaboratorPath}.avatarUrl`,
                    { maxLength: 2_048 },
                  ),
                }),
              displayName: requireString(
                collaborator.displayName,
                `${collaboratorPath}.displayName`,
                { maxLength: 120 },
              ),
              mediaCount: requireInteger(
                collaborator.mediaCount,
                `${collaboratorPath}.mediaCount`,
                { min: 1, max: 250 },
              ),
              recipientId: requireUuid(
                collaborator.recipientId,
                `${collaboratorPath}.recipientId`,
              ),
            };
          },
          {
            maxLength: 250,
            uniqueBy: (collaborator) => collaborator.recipientId,
          },
        ),
      }),
    ...(record.originalMediaCount === undefined
      ? {}
      : {
        originalMediaCount: requireInteger(
          record.originalMediaCount,
          `${path}.originalMediaCount`,
          { min: 0, max: 250 },
        ),
      }),
    ...(record.ownerAvatarUrl === undefined
      ? {}
      : {
        ownerAvatarUrl: requireString(
          record.ownerAvatarUrl,
          `${path}.ownerAvatarUrl`,
          { maxLength: 2_048 },
        ),
      }),
    ...(record.ownerEmail === undefined
      ? {}
      : {
        ownerEmail: requireString(
          record.ownerEmail,
          `${path}.ownerEmail`,
          { maxLength: 320 },
        ),
      }),
    ...(record.ownerUsername === undefined
      ? {}
      : {
        ownerUsername: requireString(
          record.ownerUsername,
          `${path}.ownerUsername`,
          { maxLength: 30 },
        ),
      }),
    revision: requireRevision(record.revision, `${path}.revision`),
    capabilities: parseClientCapabilities(
      record.capabilities,
      `${path}.capabilities`,
    ),
  };
}

function parseMediaHello(record: Record<string, unknown>): MediaHello {
  requireExactKeys(record, ["v", "type", "versions"]);
  const versions = requireArray(
    record.versions,
    "$.versions",
    (version, path) =>
      requireInteger(version, path, { min: 0, max: 255 }),
    { minLength: 1, maxLength: 8, uniqueBy: String },
  );
  if (
    versions.length !== 1
    || versions[0] !== MEDIA_PROTOCOL_VERSION
  ) {
    throw new ProtocolValidationError(
      "unsupported_version",
      "Media Protocol V2 is the only supported media version",
      "$.versions",
    );
  }
  return {
    v: MEDIA_PROTOCOL_VERSION,
    type: "media.hello",
    versions: [MEDIA_PROTOCOL_VERSION],
  };
}

function parseMediaHelloAck(record: Record<string, unknown>): MediaHelloAck {
  requireExactKeys(record, ["v", "type", "selectedVersion"]);
  requireLiteral(
    record.selectedVersion,
    MEDIA_PROTOCOL_VERSION,
    "$.selectedVersion",
  );
  return {
    v: MEDIA_PROTOCOL_VERSION,
    type: "media.hello.ack",
    selectedVersion: MEDIA_PROTOCOL_VERSION,
  };
}

function parseSharesAvailable(
  record: Record<string, unknown>,
): SharesAvailable {
  requireExactKeys(record, ["v", "type", "shares"]);
  return {
    v: MEDIA_PROTOCOL_VERSION,
    type: "shares.available",
    shares: requireArray(record.shares, "$.shares", parseAvailableShare, {
      maxLength: 20,
      uniqueBy: (share) => share.shareId,
    }),
  };
}

function parseShareUpdated(record: Record<string, unknown>): ShareUpdated {
  requireExactKeys(record, ["v", "type", "share"]);
  return {
    v: MEDIA_PROTOCOL_VERSION,
    type: "share.updated",
    share: parseAvailableShare(record.share, "$.share"),
  };
}

function parseShareRemoved(record: Record<string, unknown>): ShareRemoved {
  requireExactKeys(record, ["v", "type", "shareId", "reason"]);
  return {
    v: MEDIA_PROTOCOL_VERSION,
    type: "share.removed",
    shareId: requireUuid(record.shareId, "$.shareId"),
    reason: requireEnum(record.reason, SHARE_REMOVAL_REASONS, "$.reason"),
  };
}

function parseManifestPageGet(
  record: Record<string, unknown>,
): ManifestPageGet {
  requireExactKeys(record, [
    "v",
    "type",
    "shareId",
    "requestId",
    "after",
    "limit",
    "revision",
  ]);
  return {
    v: MEDIA_PROTOCOL_VERSION,
    type: "manifest.page.get",
    shareId: requireUuid(record.shareId, "$.shareId"),
    requestId: requireUuid(record.requestId, "$.requestId"),
    after: requireNullableString(record.after, "$.after", {
      maxLength: 1024,
    }),
    limit: requireInteger(record.limit, "$.limit", {
      min: 1,
      max: MAX_MANIFEST_PAGE_SIZE,
    }),
    revision: record.revision === null
      ? null
      : requireRevision(record.revision, "$.revision"),
  };
}

function parseManifestPage(record: Record<string, unknown>): ManifestPage {
  requireExactKeys(record, [
    "v",
    "type",
    "shareId",
    "requestId",
    "revision",
    "media",
    "nextCursor",
  ], ["totalMediaCount"]);
  return {
    v: MEDIA_PROTOCOL_VERSION,
    type: "manifest.page",
    shareId: requireUuid(record.shareId, "$.shareId"),
    requestId: requireUuid(record.requestId, "$.requestId"),
    revision: requireRevision(record.revision, "$.revision"),
    media: requireArray(record.media, "$.media", parseMediaDescriptor, {
      maxLength: MAX_MANIFEST_PAGE_SIZE,
      uniqueBy: (descriptor) => descriptor.mediaId,
    }),
    nextCursor: requireNullableString(
      record.nextCursor,
      "$.nextCursor",
      { maxLength: 1024 },
    ),
    ...(record.totalMediaCount === undefined
      ? {}
      : {
        totalMediaCount: requireInteger(
          record.totalMediaCount,
          "$.totalMediaCount",
          { min: 0 },
        ),
      }),
  };
}

function parseMediaGet(record: Record<string, unknown>): MediaGet {
  requireExactKeys(record, [
    "v",
    "type",
    "shareId",
    "requestId",
    "mediaId",
    "rendition",
  ]);
  return {
    v: MEDIA_PROTOCOL_VERSION,
    type: "media.get",
    shareId: requireUuid(record.shareId, "$.shareId"),
    requestId: requireUuid(record.requestId, "$.requestId"),
    mediaId: requireUuid(record.mediaId, "$.mediaId"),
    rendition: requireEnum(
      record.rendition,
      MEDIA_RENDITIONS,
      "$.rendition",
    ),
  };
}

function parseMediaQueued(record: Record<string, unknown>): MediaQueued {
  requireMediaRequestKeys(record);
  return {
    v: MEDIA_PROTOCOL_VERSION,
    type: "media.queued",
    shareId: requireUuid(record.shareId, "$.shareId"),
    requestId: requireUuid(record.requestId, "$.requestId"),
  };
}

function parseMediaStart(record: Record<string, unknown>): MediaStart {
  requireExactKeys(
    record,
    [
      "v",
      "type",
      "shareId",
      "requestId",
      "mediaId",
      "kind",
      "rendition",
      "mimeType",
      "byteLength",
    ],
    ["sha256"],
  );
  const result: {
    v: 2;
    type: "media.start";
    shareId: string;
    requestId: string;
    mediaId: string;
    kind: MediaStart["kind"];
    rendition: MediaStart["rendition"];
    mimeType: string;
    byteLength: number;
    sha256?: string;
  } = {
    v: MEDIA_PROTOCOL_VERSION,
    type: "media.start",
    shareId: requireUuid(record.shareId, "$.shareId"),
    requestId: requireUuid(record.requestId, "$.requestId"),
    mediaId: requireUuid(record.mediaId, "$.mediaId"),
    kind: requireEnum(record.kind, MEDIA_KINDS, "$.kind"),
    rendition: requireEnum(
      record.rendition,
      MEDIA_RENDITIONS,
      "$.rendition",
    ),
    mimeType: requireString(record.mimeType, "$.mimeType", {
      maxLength: 127,
    }),
    byteLength: requireByteLength(record.byteLength, "$.byteLength"),
  };
  if (record.sha256 !== undefined) {
    result.sha256 = requireSha256(record.sha256, "$.sha256");
  }
  return result;
}

function parseMediaProgress(record: Record<string, unknown>): MediaProgress {
  requireExactKeys(record, [
    "v",
    "type",
    "shareId",
    "requestId",
    "transferredByteLength",
  ]);
  return {
    v: MEDIA_PROTOCOL_VERSION,
    type: "media.progress",
    shareId: requireUuid(record.shareId, "$.shareId"),
    requestId: requireUuid(record.requestId, "$.requestId"),
    transferredByteLength: requireByteLength(
      record.transferredByteLength,
      "$.transferredByteLength",
    ),
  };
}

function parseMediaComplete(record: Record<string, unknown>): MediaComplete {
  requireExactKeys(record, [
    "v",
    "type",
    "shareId",
    "requestId",
    "byteLength",
    "sha256",
  ]);
  return {
    v: MEDIA_PROTOCOL_VERSION,
    type: "media.complete",
    shareId: requireUuid(record.shareId, "$.shareId"),
    requestId: requireUuid(record.requestId, "$.requestId"),
    byteLength: requireByteLength(record.byteLength, "$.byteLength"),
    sha256: requireSha256(record.sha256, "$.sha256"),
  };
}

function parseMediaCancel(record: Record<string, unknown>): MediaCancel {
  requireExactKeys(record, [
    "v",
    "type",
    "shareId",
    "requestId",
    "reason",
  ]);
  return {
    v: MEDIA_PROTOCOL_VERSION,
    type: "media.cancel",
    shareId: requireUuid(record.shareId, "$.shareId"),
    requestId: requireUuid(record.requestId, "$.requestId"),
    reason: requireEnum(record.reason, MEDIA_CANCEL_REASONS, "$.reason"),
  };
}

function parseMediaError(record: Record<string, unknown>): MediaError {
  requireExactKeys(record, [
    "v",
    "type",
    "shareId",
    "requestId",
    "code",
    "retryable",
  ]);
  return {
    v: MEDIA_PROTOCOL_VERSION,
    type: "media.error",
    shareId: requireUuid(record.shareId, "$.shareId"),
    requestId: requireUuid(record.requestId, "$.requestId"),
    code: requireEnum(record.code, MEDIA_ERROR_CODES, "$.code"),
    retryable: requireBoolean(record.retryable, "$.retryable"),
  };
}

function requireMediaRequestKeys(record: Record<string, unknown>): void {
  requireExactKeys(record, ["v", "type", "shareId", "requestId"]);
}

function requireMediaVersion(value: unknown): void {
  if (value !== MEDIA_PROTOCOL_VERSION) {
    throw new ProtocolValidationError(
      "unsupported_version",
      `Unsupported media protocol version: ${String(value)}`,
      "$.v",
    );
  }
}

function requireRevision(value: unknown, path: string): string {
  return requireString(value, path, { maxLength: 256 });
}

function wrongDirection(
  type: string,
  expectedSender: string,
): ProtocolValidationError {
  return new ProtocolValidationError(
    "unknown_type",
    `${type} is not valid from the ${expectedSender}`,
    "$.type",
  );
}
